import { useEffect, useState } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { apiErrorMessage, apiUrl, getInvitePreview, startInvite, getToken, type InvitePreview as InvitePreviewData, type InterviewPlan } from '@/api/client'
import { useAuth } from '@/contexts/AuthContext'
import { InterviewAgent } from '@/components/voice/InterviewAgent'
import { AmbientVideo } from '@/components/AmbientVideo'
import { VoicePicker } from '@/components/interview/VoicePicker'
import { useVoiceCatalog } from '@/hooks/use-voice-catalog'
import { Toaster } from '@/components/ui/toaster'
import { useToast } from '@/hooks/use-toast'
import { initials, formatDate, formatDateTime, isExpired } from '@/lib/dashboard-utils'
import { usePageMeta } from '@/hooks/use-page-meta'
import '@/styles/aura-pre.css'
// The invite page renders shell-less but borrows aura-app's wspinner helper.
import '@/styles/aura-app.css'

type Step = 'PREVIEW' | 'INTERVIEW'

/**
 * The energy-sphere stage every state of the invite page sits on (promoted
 * from the "Stage" prototype round). Same reduced-motion contract as the
 * auth shell: the video pauses and CSS hides it, leaving the static glow.
 */
function StageBackdrop() {
  return (
    <div className="pre-media" aria-hidden="true">
      <AmbientVideo className="pre-video" />
      <div className="pre-scrim"></div>
    </div>
  )
}

export function InviteInterview() {
  usePageMeta("Interview invitation — Aura", { noindex: true })
  const { token } = useParams<{ token: string }>()
  const { user, login } = useAuth()
  const navigate = useNavigate()
  const { toast } = useToast()

  const [preview, setPreview] = useState<InvitePreviewData | null>(null)
  const [plan, setPlan] = useState<InterviewPlan | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [livekitToken, setLivekitToken] = useState<string | null>(null)
  const [step, setStep] = useState<Step>('PREVIEW')
  const [loading, setLoading] = useState(true)
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const { voices, voiceId, handleVoiceChange, resolveJoinVoice } = useVoiceCatalog()
  // Bumped by the expiry timer below; without it a preview loaded before
  // expiry keeps saying "This link expires …" until some other re-render.
  const [, setExpiryTick] = useState(0)

  // Remember where to come back to after sign-in / role picking
  useEffect(() => {
    if (!user || user.role === '') {
      if (token) sessionStorage.setItem('aura_return_to', `/invite/${token}`)
    }
  }, [user, token])

  useEffect(() => {
    if (!user || !token) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    getInvitePreview(token)
      .then(data => { if (!cancelled) setPreview(data) })
      .catch(err => {
        if (cancelled) return
        setError(apiErrorMessage(err, 'This invite link is not available.'))
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [user, token])

  // A preview loaded before expiry never refetches on its own; flip the expiry
  // copy and disable Start the moment the deadline passes. Beyond setTimeout's
  // ~24.8-day ceiling (links can run 30 days) just skip the timer — a start
  // click still fails with the server's 410 toast.
  useEffect(() => {
    const ms = preview?.expires_at ? new Date(preview.expires_at).getTime() - Date.now() : 0
    if (ms <= 0 || ms > 2 ** 31 - 1) return
    const timer = setTimeout(() => setExpiryTick(t => t + 1), ms + 1000)
    return () => clearTimeout(timer)
  }, [preview])

  const begin = async () => {
    if (!token || !preview) return
    setStarting(true)
    setError(null)
    try {
      const started = await startInvite(token)
      const t = await getToken(started.session_id, resolveJoinVoice())
      setSessionId(started.session_id)
      setPlan(started.plan)
      setLivekitToken(t)
      setStep('INTERVIEW')
    } catch (err) {
      toast({
        title: 'Could not start the interview',
        description: apiErrorMessage(err, 'Please try again in a moment.'),
        variant: 'destructive',
      })
    } finally {
      setStarting(false)
    }
  }

  // ---- Not signed in: stage teaser + sign-in buttons ----
  if (!user) {
    return (
      <div className="aura-pre-page">
        <StageBackdrop />
        <main className="pre-inner">
          <p className="pre-kicker">Interview invitation</p>
          <h1 className="pre-title">Sign in to begin</h1>
          <p className="pre-lede">
            You've been invited to a voice interview. Sign in — your answers and the
            report stay private between you and the recruiter.
          </p>
          <div className="pre-oauth">
            <button type="button" className="btn btn-ghost" onClick={() => login('google')}>
              Continue with Google
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => login('github')}>
              Continue with GitHub
            </button>
          </div>
          <p className="pre-note">You'll return to this invite automatically after signing in.</p>
        </main>
        <Toaster />
      </div>
    )
  }

  // ---- Signed in but no role chosen yet ----
  if (user.role === '') {
    return <Navigate to="/choose-role" replace />
  }

  // ---- Live interview ----
  if (step === 'INTERVIEW' && livekitToken && sessionId && plan) {
    return (
      <InterviewAgent
        token={livekitToken}
        sessionId={sessionId}
        candidateName={plan.candidate_name}
        recordAudio
        proctoring={preview?.proctoring}
        onInterviewEnd={() => navigate(`/my-interviews/${sessionId}`, { replace: true })}
      />
    )
  }

  // ---- Preview: loading, unavailable, or the invitation itself ----
  const expired = preview ? isExpired(preview.expires_at) : false
  return (
    <div className="aura-pre-page">
      <StageBackdrop />
      <main className="pre-inner">
        {loading ? (
          <div className="pre-loading" role="status" aria-live="polite">
            <span className="wspinner" aria-hidden="true"></span>
            <span>Loading your invitation…</span>
          </div>
        ) : error || !preview ? (
          <div className="pre-unavailable aura-panel-in">
            <h1>Invite unavailable</h1>
            <p>{error || 'This invite link is not valid.'}</p>
            <Link to="/" className="btn btn-primary">Go home</Link>
          </div>
        ) : (
          <>
            <p className="pre-kicker">Interview invitation</p>
            <h1 className="pre-title">{preview.title}</h1>

            <div className="pre-by">
              <span className="pre-avatar" aria-hidden="true">{initials(preview.recruiter_name || 'R')}</span>
              <span>
                with <strong>{preview.recruiter_name}</strong>
                {preview.recruiter_company ? ` · ${preview.recruiter_company}` : ''}
              </span>
            </div>

            <p className="pre-meta">
              <span>{preview.questions.length} questions</span>
              <span aria-hidden="true">·</span>
              <span>Voice interview</span>
              <span aria-hidden="true">·</span>
              <span className={expired ? 'is-expired' : undefined}>
                {expired
                  ? 'Link expired'
                  : preview.expires_at
                    ? `Valid until ${formatDate(preview.expires_at)}`
                    : 'No expiry'}
              </span>
            </p>

            {/* The deadline as a calendar entry: the .ics is public (the token
                is the secret), so it works before any sign-in. */}
            {token && preview.expires_at && !expired && (
              <p className="pre-note">
                <a href={apiUrl(`/invite/${token}/calendar.ics`)} className="pre-cal-link">
                  Add the deadline to your calendar
                </a> — the link stops working after it passes.
              </p>
            )}

            <div className="pre-voice">
              <VoicePicker voices={voices} value={voiceId} onChange={handleVoiceChange} />
            </div>

            {preview.proctoring && (
              <p className="pre-proctor-note" role="note">
                This interview is proctored: tab and focus changes are recorded, and your
                spoken answers are also analysed for signs of AI-generated or rehearsed
                responses. Only these signals are shared with {preview.recruiter_name} —
                there's no camera and no automated judgment.
              </p>
            )}

            <div className="pre-cta">
              <button type="button" className="btn btn-primary" onClick={begin} disabled={starting || expired}>
                {starting ? 'Preparing…' : 'Begin voice interview'}
              </button>
              <button
                type="button"
                className="pre-toggle"
                aria-expanded={detailsOpen}
                aria-controls="invite-details"
                onClick={() => setDetailsOpen(o => !o)}
              >
                <span>{detailsOpen ? 'Hide the details' : "See what you'll be asked"}</span>
                <svg className={detailsOpen ? 'is-open' : undefined} viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>

            <div className={detailsOpen ? 'pre-panel is-open' : 'pre-panel'}>
              <div className="pre-panel-clip" id="invite-details">
                <div className="pre-panel-body">
                  {preview.context && (
                    <section>
                      <h2>About this role</h2>
                      <p>{preview.context}</p>
                    </section>
                  )}
                  <section>
                    <h2>You'll be asked, live and in order</h2>
                    <ol className="pre-questions">
                      {preview.questions.map((q, i) => (
                        <li key={i}>
                          <span className="pre-qnum" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
                          <span>{q}</span>
                        </li>
                      ))}
                    </ol>
                  </section>
                  <p className="pre-note">
                    {expired
                      ? 'This link has expired — ask your recruiter for a fresh one. '
                      : preview.expires_at
                        ? `This link expires ${formatDateTime(preview.expires_at)}. `
                        : ''}
                    Your microphone is recorded so {preview.recruiter_name} can review the
                    conversation — your answers and report stay private between you.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </main>
      <Toaster />
    </div>
  )
}
